import { Inject, Injectable, Injector, LOCALE_ID } from '@angular/core';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { IBaseResponse } from '@carm/_utils/models';
import { Observable } from 'rxjs';
import {
  IModifyRulingRequest,
  IModifyRulingResponse,
  IRulingDraftDetailGetResponse,
  IRulingDraftListResponse,
  IRulingDraftSaveRequest,
  IRulingListResponse,
} from '../models';
import { DatePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import * as dayjs from 'dayjs';
import { dateFormat } from '@carm/shared/constants/const-carm';
import { AdvancedSearchCheckboxes, AdvancedSearchConfigResponse, AdvancedSearchOption } from '@carm/shared/components/advanced-search/models/advanced-search.model';
import { map, mergeMap } from 'rxjs/operators';
import { IRulingSubmittedResponse } from '../models/ruling-draft-api.model';
import { LoaderSpinnerTypes } from '@carm/_core/models/loader-types.model';
import { MetadataService } from '@carm/_core/metadata/services';

@Injectable()
export class RulingsService extends BaseApiService {
  
  private metadataService: MetadataService;
  
  constructor(
    protected injector: Injector,
    private datePipe: DatePipe,
    @Inject(LOCALE_ID) protected localeId: string
  ) {
    super(injector);
    this.metadataService = injector.get(MetadataService);
  }

  public getRulingsDrafts(
    startDate: Date,
    endDate: Date
  ): Observable<IRulingDraftListResponse> {
    let endPoint = `${this.endPointBP}/rulings/drafts/list`;
    const params = new HttpParams()
      .set('startDate', this.datePipe.transform(startDate, 'yyyy-MM-dd'))
      .set('endDate', this.datePipe.transform(endDate, 'yyyy-MM-dd'));

    return this.httpAuthGet<IRulingDraftListResponse>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  public getRulingDraftById(
    rulingDraftUid: string
  ): Observable<IRulingDraftDetailGetResponse> {
    const endPoint = `${this.endPointBP}/rulings/drafts/rulingdraft/${rulingDraftUid}/edit`;
    return this.httpAuthGet<IRulingDraftDetailGetResponse>(endPoint).pipe(
      mergeMap(draft => {
        return this.metadataService.getRulingEntryPorts().pipe(
          map(x => {
            if (draft.commonForm?.portEntry) {
              draft.commonForm.portEntryText = x.find(c => c.key === draft.commonForm?.portEntry).value;
            }
            return {
              ...draft
            } as IRulingDraftDetailGetResponse
          })
        )
    }));
  }

  public getRulingById(
    caseNumber: string
  ): Observable<IRulingDraftDetailGetResponse> {
    const endPoint = `${this.endPointBP}/rulings/${caseNumber}?fields=DEFAULT`;
    return this.httpAuthGet<IRulingDraftDetailGetResponse>(endPoint);
  }

  public getRulings(
    searchString?: string,
    minDate?: Date,
    maxDate?: Date,
    sortField?: string,
    currentPage?: number,
    pageSize?: number
  ): Observable<IRulingListResponse> {
    const endPoint = `${this.endPointBP}/rulings/list`;
    const params = this.getHttpPameter(
      searchString,
      minDate,
      maxDate,
      sortField,
      currentPage,
      pageSize
    );
    return this.httpAuthGet<IRulingListResponse>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  public withdrawRuling(caseNumber: string, withdrawReason: string) {
    const endPoint = `${this.endPointBP}/rulings/withdraw`;
    return this.httpAuthPost(endPoint, { caseNumber, withdrawReason });
  }

  public saveDraft(draftRuling: IRulingDraftSaveRequest): Observable<IBaseResponse> {
    const endPoint = `${this.endPointBP}/rulings/drafts/rulingdraft/save`;
    return this.httpAuthPost<IBaseResponse>(endPoint, draftRuling);
  }

  public submitRuling(draftRuling: IRulingDraftSaveRequest): Observable<IRulingSubmittedResponse> {
    const endPoint = `${this.endPointBP}/rulings/submitruling`;
    return this.httpAuthPost<IRulingSubmittedResponse>(endPoint, draftRuling);
  }

  deleteDraft(draftId: string): Observable<IBaseResponse> {
    const endPoint = `${this.endPointBP}/rulings/drafts/rulingdraft/${draftId}/delete`;
    return this.httpAuthDelete<IBaseResponse>(endPoint);
  }

  modifyRuling(
    modifyRuling: IModifyRulingRequest
  ): Observable<IModifyRulingResponse> {
    const endPoint = `${this.endPointBP}/rulings/modify`;
    return this.httpAuthPost<IModifyRulingResponse>(endPoint, modifyRuling);
  }

  private getHttpPameter(
    searchString: string,
    startDate: Date,
    endDate: Date,
    sortField: string,
    currentPage: number,
    pageSize: number
  ): HttpParams {
    const params = new HttpParams()
      .set('currentPage', currentPage ? currentPage.toString() : '1')
      .set('pageSize', pageSize ? pageSize.toString() : '100')
      .set(
        'startDate',
        startDate
          ? dayjs(startDate).format(dateFormat)
          : dayjs().subtract(100, 'years').format(dateFormat)
      ) // default to today - 100years
      .set(
        'endDate',
        endDate
          ? dayjs(endDate).format(dateFormat)
          : dayjs().format(dateFormat)
      ) // default to today + 1 day
      .set('declarationsSort', sortField ? sortField : '')
      .set('searchString', searchString || null);

    return params;
  }

  getRulingsSearchOptions(): Observable<AdvancedSearchConfigResponse> {
    const endPoint = `${this.endPointBP}/rulings/search`;
    return this.httpAuthGet<AdvancedSearchConfigResponse>(endPoint).pipe(
      map((advanceSearch: AdvancedSearchConfigResponse) => {
        return {
          searchOptions: advanceSearch.searchOptions?.map(
            (advanceSearchOption: AdvancedSearchOption) => {
              advanceSearchOption.key = advanceSearchOption.value; // api return number for key. use identifier
              advanceSearchOption.value = this.translateService.instant(
                `concepts.declarations.advanceSearchOptions.${advanceSearchOption.value}`
              );
              return advanceSearchOption;
            }
          ),
          checkboxes: advanceSearch.checkboxes?.map(
            (checkboxes: AdvancedSearchCheckboxes) => {
              checkboxes.value = this.translateService.instant(
                `concepts.declarations.advanceSearchOptions.${checkboxes.value}`
              );
              return checkboxes;
            }
          ),
        } as AdvancedSearchConfigResponse;
      })
    );
  }
}
